import Quill, { Delta } from 'quill';

import { isNotEmpty, sanitizeEmptyContent } from '@/shared/util';

import { normalizeNestedListsForQuillPaste, shouldNormalizeNestedListsForQuill } from './list';
import { removeStyleAttributesFromElement, removeStyleAttributesFromHtml } from './style.util';

function removeAlignAttributeFromDelta(delta: Delta): Delta {
  let hasAlignAttribute = false;

  const nextOps = delta.ops?.map((operation) => {
    if (!operation.attributes || !Object.hasOwn(operation.attributes, 'align')) {
      return operation;
    }

    hasAlignAttribute = true;

    const remainingAttributes = { ...operation.attributes };

    delete remainingAttributes.align;

    if (Object.keys(remainingAttributes).length === 0) {
      const nextOperation = { ...operation };

      delete nextOperation.attributes;

      return nextOperation;
    }

    return {
      ...operation,
      attributes: remainingAttributes,
    };
  });

  if (!hasAlignAttribute) {
    return delta;
  }

  return new Delta(nextOps ?? []);
}

function removeStyleAttributesMatcher(node: Node, delta: Delta): Delta {
  if (node instanceof HTMLElement) {
    removeStyleAttributesFromElement(node);
  }

  return removeAlignAttributeFromDelta(delta);
}

interface QuillConfigOptions {
  formats: string[];
  isReadOnly: boolean;
  placeholder: string;
}

interface CharacterLimitMatcherOptions {
  getCurrentLength: () => number;
  getCurrentSelectionLength: () => number;
  getMaxLength: () => number | undefined;
}

export function createQuillConfig({ formats, isReadOnly, placeholder }: QuillConfigOptions) {
  return {
    debug: 'error' as const,
    formats,
    modules: {
      keyboard: {
        bindings: {
          tab: {
            key: 9,
            handler: () => true,
          },
        },
      },
      toolbar: isNotEmpty(formats) ? [['bold', 'underline'], [{ list: 'bullet' }]] : false,
    },
    placeholder,
    readOnly: isReadOnly,
    theme: 'bubble',
  };
}

function registerNestedListPasteNormalizer(quill: Quill): void {
  if (!quill.root?.addEventListener) {
    return;
  }

  const handlePaste = (event: ClipboardEvent) => {
    if (!quill.isEnabled()) {
      return;
    }

    const html = event.clipboardData?.getData('text/html');

    if (!html || !shouldNormalizeNestedListsForQuill(html)) {
      return;
    }

    const normalizedHtml = normalizeNestedListsForQuillPaste(html);

    if (normalizedHtml === html) {
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();

    const selection = quill.getSelection(true);
    const index = selection?.index ?? Math.max(0, quill.getLength() - 1);
    const length = selection?.length ?? 0;
    const pastedDelta = quill.clipboard.convert({ html: normalizedHtml });

    quill.updateContents(new Delta().retain(index).delete(length).concat(pastedDelta), 'user');

    const nextSelectionIndex = index + pastedDelta.length();

    quill.setSelection(nextSelectionIndex, 0, 'silent');
  };

  quill.root.addEventListener('paste', handlePaste, true);
}

export function registerClipboardMatchers(quill: Quill): void {
  quill.clipboard.addMatcher(Node.ELEMENT_NODE, removeStyleAttributesMatcher);
  registerNestedListPasteNormalizer(quill);
}

function getOperationLength(insert: Delta['ops'][number]['insert']): number {
  if (typeof insert === 'string') {
    return insert.length;
  }

  return 1;
}

function truncateDelta(delta: Delta, maxLength: number): Delta {
  if (maxLength <= 0) {
    return new Delta();
  }

  const nextOps: Delta['ops'] = [];
  let remainingCharacters = maxLength;

  for (const operation of delta.ops ?? []) {
    if (remainingCharacters <= 0) {
      break;
    }

    if (typeof operation.insert === 'string') {
      const truncatedInsert = operation.insert.slice(0, remainingCharacters);

      if (!truncatedInsert.length) {
        continue;
      }

      nextOps.push({
        ...operation,
        insert: truncatedInsert,
      });
      remainingCharacters -= truncatedInsert.length;

      continue;
    }

    nextOps.push(operation);
    remainingCharacters -= getOperationLength(operation.insert);
  }

  return new Delta(nextOps);
}

interface InsertRange {
  length: number;
  start: number;
}

function getInsertRanges(delta: Delta): InsertRange[] {
  const insertRanges: InsertRange[] = [];
  let indexInNewContent = 0;

  for (const operation of delta.ops ?? []) {
    if (typeof operation.retain === 'number') {
      indexInNewContent += operation.retain;
    }

    if (operation.insert) {
      const operationLength = getOperationLength(operation.insert);

      insertRanges.push({
        length: operationLength,
        start: indexInNewContent,
      });
      indexInNewContent += operationLength;
    }
  }

  return insertRanges;
}

function trimOverflowFromInsertedContent({
  delta,
  overflowCount,
  quill,
}: {
  delta: Delta;
  overflowCount: number;
  quill: Quill;
}): boolean {
  if (overflowCount <= 0) {
    return true;
  }

  const insertRanges = getInsertRanges(delta);
  let remainingOverflow = overflowCount;

  for (const insertRange of [...insertRanges].reverse()) {
    if (remainingOverflow <= 0) {
      break;
    }

    const removeCount = Math.min(insertRange.length, remainingOverflow);
    const removeStart = insertRange.start + insertRange.length - removeCount;

    quill.deleteText(removeStart, removeCount, 'silent');
    remainingOverflow -= removeCount;
  }

  return remainingOverflow <= 0;
}

export function createCharacterLimitMatcher({
  getCurrentLength,
  getCurrentSelectionLength,
  getMaxLength,
}: CharacterLimitMatcherOptions) {
  return (_node: Node, delta: Delta): Delta => {
    const maxLength = getMaxLength();

    if (typeof maxLength !== 'number') {
      return delta;
    }

    const currentLength = getCurrentLength();
    const currentSelectionLength = Math.max(0, getCurrentSelectionLength());
    const effectiveCurrentLength = Math.max(0, currentLength - currentSelectionLength);
    const remainingCharacters = maxLength - effectiveCurrentLength;

    return truncateDelta(delta, remainingCharacters);
  };
}

export function registerCharacterLimitClipboardMatcher(
  quill: Quill,
  options?: CharacterLimitMatcherOptions,
): void {
  if (!options) {
    return;
  }

  quill.clipboard.addMatcher('BODY', createCharacterLimitMatcher(options));
}

export function enforceQuillMaxLength(
  quill: Quill,
  maxLength: number,
  changeDelta?: Delta,
): number {
  const currentLength = Math.max(0, quill.getText().length - 1);

  if (currentLength <= maxLength) {
    return currentLength;
  }

  const overflowCount = currentLength - maxLength;

  if (
    changeDelta &&
    trimOverflowFromInsertedContent({
      delta: changeDelta,
      overflowCount,
      quill,
    })
  ) {
    return maxLength;
  }

  const selection = quill.getSelection();
  const truncatedDelta = quill.getContents(0, maxLength);

  quill.setContents(truncatedDelta, 'silent');

  if (selection) {
    const clampedIndex = Math.min(selection.index, maxLength);
    const maxSelectableLength = Math.max(0, maxLength - clampedIndex);
    const clampedLength = Math.min(selection.length, maxSelectableLength);

    quill.setSelection(clampedIndex, clampedLength, 'silent');
  }

  return maxLength;
}

export function hasContentChanged(quill: Quill, formattedValue: string): boolean {
  return formattedValue !== quill.root.innerHTML;
}

export function updateQuillContentWithSelection(quill: Quill, formattedValue: string): void {
  const selection = quill.getSelection();
  const html = quill.clipboard.convert({
    html: formattedValue,
  });

  quill.setContents(html, 'silent');

  if (selection) {
    requestAnimationFrame(() => {
      quill.setSelection(selection);
    });
  }
}

export function getCleanedHtmlFromQuill(quill: Quill): string {
  const html = quill.root.innerHTML || '';
  const cleanedHtml = removeStyleAttributesFromHtml(html);

  return sanitizeEmptyContent(cleanedHtml);
}

export function calculateEditedText(
  initialValue: string,
  currentValue: string,
  propValue: string | undefined,
): string {
  return initialValue !== currentValue && propValue !== currentValue ? currentValue : '';
}
